/**
 * PodReceiptCard — presentational Proof-of-Delivery receipt card.
 *
 * Single source of truth for the receipt layout, rendered by both:
 *   - PodReceiptPage (online, public /pod/:id) — fed fetched `data` + media blobs.
 *   - DesktopReceiptModal (desktop app) — fed the order via the adapter and
 *     authenticated media URLs from either the cloud or protected local backend.
 *
 * All DB-backed fields render identically in both modes.
 */
import React from 'react';
import { MapPin, Clock, User, Truck, FileText, ExternalLink, ImageOff } from 'lucide-react';
import { offlineMediaLabel } from '@/utils/mediaPresentation';

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
};

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const fieldLabel = (field) => {
  const labels = { received_by: 'Received by', received_by_relationship: 'Relationship', notes: 'Notes', photos: 'Photos', video: 'Video' };
  return labels[field] || field;
};

const OfflinePlaceholder = ({ label, state }) => (
  <div
    className="flex items-center gap-2 rounded-lg border border-dashed border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-400"
    data-testid="pod-media-offline"
  >
    <ImageOff className="h-3.5 w-3.5 flex-shrink-0" />
    {`${label} — ${offlineMediaLabel(state)}`}
  </div>
);

export default function PodReceiptCard({
  data,
  orderId,
  mediaUrls = {},
  mediaStates = {},
  arrangementUrl = null,
  arrangementState = null,
  offline = false,
  onImageClick = () => {},
}) {
  const c = data.confirmation || {};
  const mediaFiles = c.media_files || [];
  const signatureIdx = mediaFiles.findIndex((f) => f.file_type === 'signature');
  const photoIndices = mediaFiles.reduce((acc, f, i) => (f.file_type === 'photo' ? [...acc, i] : acc), []);
  const videoIdx = mediaFiles.findIndex((f) => f.file_type === 'video');

  const gps = c.gps_location;
  const hasGps = gps?.latitude && gps?.longitude && !(gps.latitude === 0 && gps.longitude === 0);
  const gpsLink = hasGps ? `https://www.google.com/maps?q=${gps.latitude},${gps.longitude}` : null;

  const mediaUrl = (index) => mediaUrls[index];

  const fullAddress = [
    data.recipient_street_address,
    data.recipient_street_address_2,
    [data.recipient_city, data.recipient_state, data.recipient_zip_code].filter(Boolean).join(', '),
  ].filter(Boolean).join(', ');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden print:shadow-none print:border-0 print:rounded-none" data-testid="pod-receipt-card">
      {/* Header */}
      <div className="bg-green-700 px-6 py-5 text-white print:bg-white print:text-stone-900 print:border-b-2 print:border-green-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight">Las Vegas Bouquet</h1>
            <p className="text-green-200 text-sm print:text-stone-500">1815 W. Charleston Blvd, Ste. 6, Las Vegas, NV 89102</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-green-100 print:text-stone-600">Delivery Receipt</p>
            {data.internal_order_number && (
              <p className="text-lg font-mono font-bold mt-0.5">#{data.internal_order_number}</p>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Delivery info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">Delivered to</p>
            <p className="text-base font-semibold text-stone-900" data-testid="pod-receipt-recipient">{data.recipient_name || 'N/A'}</p>
            <p className="text-sm text-stone-600 mt-0.5">{fullAddress || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">Delivery Date</p>
            <p className="text-base font-semibold text-stone-900">{fmtDate(data.delivery_date) || 'N/A'}</p>
            {data.resolved_source && (
              <p className="text-sm text-stone-500 mt-0.5">Source: {data.resolved_source}</p>
            )}
          </div>
        </div>

        <hr className="border-stone-200" />

        {/* POD details */}
        <div>
          <h2 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-green-600" />
            Proof of Delivery
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="flex items-start gap-2.5">
                <User className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-stone-400">Received by</p>
                  <p className="text-sm font-medium text-stone-900" data-testid="pod-receipt-received-by">
                    {c.received_by ? (
                      <>
                        {c.received_by}
                        {c.received_by_relationship && (
                          <span className="text-stone-500 font-normal"> ({c.received_by_relationship})</span>
                        )}
                      </>
                    ) : (
                      <span className="text-stone-400 italic">Not recorded</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Clock className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-stone-400">Delivered at</p>
                  <p className="text-sm font-medium text-stone-900" data-testid="pod-receipt-time">
                    {fmtTime(c.time_delivered || c.confirmed_at) || <span className="text-stone-400 italic">Not recorded</span>}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Truck className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-stone-400">Driver</p>
                  <p className="text-sm font-medium text-stone-900" data-testid="pod-receipt-driver">
                    {data.driver_name || c.confirmed_by_user_name || <span className="text-stone-400 italic">Not recorded</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* GPS location + minimap */}
            <div className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-stone-400">GPS Location</p>
                {gpsLink ? (
                  <a
                    href={gpsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-green-700 hover:underline inline-flex items-center gap-1 print:text-stone-900"
                    data-testid="pod-receipt-gps-link"
                  >
                    {Number(gps.latitude).toFixed(5)}, {Number(gps.longitude).toFixed(5)}
                    <ExternalLink className="h-3 w-3 print:hidden" />
                  </a>
                ) : (
                  <p className="text-sm text-stone-500">Not captured</p>
                )}
                {gps?.accuracy && (
                  <p className="text-xs text-stone-400">±{Math.round(Number(gps.accuracy))}m</p>
                )}
                {gpsLink && (
                  offline ? (
                    <div className="mt-2 w-64"><OfflinePlaceholder label="Location map" /></div>
                  ) : (
                    <a href={gpsLink} target="_blank" rel="noopener noreferrer" className="block mt-2 rounded-lg overflow-hidden border border-stone-200 hover:border-stone-300 transition-colors" data-testid="pod-receipt-static-map">
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${gps.latitude},${gps.longitude}&zoom=15&size=400x250&scale=2&markers=color:red|${gps.latitude},${gps.longitude}&key=${process.env.REACT_APP_GOOGAPI_FRONT}`}
                        alt="Delivery location map"
                        className="w-64 h-40 object-cover"
                        loading="lazy"
                      />
                    </a>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {c.notes && (
          <div className="bg-stone-50 rounded-xl px-4 py-3 border border-stone-100">
            <p className="text-xs text-stone-400 mb-1">Driver Notes</p>
            <p className="text-sm text-stone-800 whitespace-pre-line" data-testid="pod-receipt-notes">{c.notes}</p>
          </div>
        )}

        {/* Signature */}
        {signatureIdx >= 0 && (offline || mediaUrl(signatureIdx)) && (
          <div>
            <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">Signature</p>
            {mediaUrl(signatureIdx) ? (
              <img
                src={mediaUrl(signatureIdx)}
                alt="Signature"
                className="h-24 rounded-lg border border-stone-200 bg-white cursor-pointer hover:opacity-80 transition-opacity print:cursor-default"
                onClick={() => onImageClick(mediaUrl(signatureIdx))}
                data-testid="pod-receipt-signature"
              />
            ) : (
              <OfflinePlaceholder label="Signature" state={mediaStates[signatureIdx]} />
            )}
          </div>
        )}

        {/* Arrangement beside the driver's delivery photos */}
        {(data.has_arrangement_photo || photoIndices.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.has_arrangement_photo && (offline || arrangementUrl) && (
              <div data-testid="pod-receipt-arrangement">
                <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">Your Arrangement</p>
                {arrangementUrl ? (
                  <img
                    src={arrangementUrl}
                    alt="Completed arrangement"
                    className="w-full max-h-80 object-contain rounded-xl border border-stone-200 bg-white cursor-pointer hover:opacity-90 transition-opacity print:cursor-default"
                    onClick={() => onImageClick(arrangementUrl)}
                  />
                ) : (
                  <OfflinePlaceholder label="Arrangement photo" state={arrangementState} />
                )}
              </div>
            )}

            {photoIndices.length > 0 && (
              <div>
                <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">
                  Delivery Photos ({photoIndices.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {photoIndices.filter((idx) => mediaUrl(idx)).map((idx, i) => (
                      <img
                        key={idx}
                        src={mediaUrl(idx)}
                        alt={`Delivery photo ${i + 1}`}
                        className="h-24 w-24 object-cover rounded-lg border border-stone-200 cursor-pointer hover:opacity-80 transition-opacity print:cursor-default"
                        onClick={() => onImageClick(mediaUrl(idx))}
                        data-testid={`pod-receipt-photo-${i}`}
                      />
                  ))}
                  {offline && photoIndices.some((idx) => !mediaUrl(idx)) && (
                    <OfflinePlaceholder
                      label={`${photoIndices.filter((idx) => !mediaUrl(idx)).length} delivery photo${photoIndices.filter((idx) => !mediaUrl(idx)).length === 1 ? '' : 's'}`}
                      state={photoIndices
                        .map((idx) => mediaStates[idx])
                        .find((result) => result?.state === 'loading')
                        || photoIndices.map((idx) => mediaStates[idx]).find(Boolean)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Video */}
        {videoIdx >= 0 && (offline || mediaUrl(videoIdx)) && (
          <div className={mediaUrl(videoIdx) ? 'print:hidden' : ''}>
            <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2">Video</p>
            {mediaUrl(videoIdx) ? (
              <video
                src={mediaUrl(videoIdx)}
                controls
                className="max-h-56 rounded-lg border border-stone-200"
                data-testid="pod-receipt-video"
              />
            ) : (
              <OfflinePlaceholder label="Video" state={mediaStates[videoIdx]} />
            )}
          </div>
        )}

        {/* Footer timestamp */}
        <div className="pt-4 border-t border-stone-200 flex items-center justify-between text-xs text-stone-400">
          <span>Confirmation submitted {fmtTime(c.time_confirmed || c.confirmed_at)}</span>
          {orderId && <span className="font-mono">ID: {orderId.slice(0, 8)}</span>}
        </div>

        {/* Edit History Log */}
        {c.edit_history?.length > 0 && (
          <div className="pt-3 border-t border-dashed border-stone-200" data-testid="pod-edit-history">
            <p className="text-[10px] font-medium text-stone-400 uppercase tracking-wider mb-2">Edit History</p>
            <div className="space-y-1.5">
              {c.edit_history.map((entry, i) => {
                const isMedia = entry.field === 'photos' || entry.field === 'video';
                return (
                  <div key={`${entry.timestamp}-${entry.field}-${i}`} className="flex items-start gap-2 text-xs text-stone-500">
                    <span className="text-stone-400 flex-shrink-0 w-[130px]">{fmtTime(entry.timestamp)}</span>
                    <span className="font-medium text-stone-600 flex-shrink-0">{entry.user}</span>
                    <span>
                      {isMedia ? 'added ' : 'changed '}
                      <span className="font-medium text-stone-600">{fieldLabel(entry.field)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
